import { LandingModelState } from "../models/LandingModel/landing.model";
import apiConvocatorias from "../api/Convocatorias/api-convocatorias";
import { useState } from "react";
import moment from "moment";
import apiMaestras from "../api/Maestras/api-maestras";
import { generarAnios } from "../utils/generateYears";
import { EntidadOptions } from "../utils/dataSelectOptions"
import { filtrarPorBusqueda } from "../utils/busquedaConvocatorias";

const initialState = {
  todaslasconvocatorias: [],
  convocatoriasslice: [],
  convocatoriasfiltradas: [],
  cantidad: 100,
  incremento: 100,
  perfil: [],
  area: [],
  entidad: [],
  linea_estrategicas: [],
  linea_estrategica: "",
  categoria_linea_convocatoria: "",
  categoria_linea_convocatorias: [],
  palabra_clave: "",
  buscarconvocatorias: {
    perfil_participante: null,
    area: null,
    linea_convocatoria: null,
    categoria_linea_convocatoria: null,
    buscar_palabra_clave: null,
    tipo_convocatoria: null,
    anio: null,
    entidad: null
  },
  anios: []
};

export const useLanding = () => {
  const [principalState, setPrincipalState] = useState<LandingModelState>(initialState);

  const [visibleButton, setVisibleButton] = useState(true);

  const [loadingConvocatorias, setLoadingConvocatorias] = useState<boolean>(true);

  /**
   * Un maestro que no se pudo traer no deja sin listado a la pantalla.
   *
   * Las tres peticiones iban en cadena y sin red de seguridad: un 400 en
   * /areas tiraba toda la carga, la pantalla se quedaba sin convocatorias y el
   * indicador de "cargando" no se apagaba nunca. Los maestros acompañan al
   * listado, pero no son el listado.
   */
  const maestroOpcional = async (pedir: () => Promise<any>, cual: string): Promise<any> => {
    try {
      return await pedir();
    } catch (error) {
      console.warn(`No se pudo traer ${cual}; se sigue sin ese filtro.`, error);
      return [];
    }
  };

  const getConvocatorias = async () => {
    setLoadingConvocatorias(true);

    try {
      const { convocatorias } = await apiConvocatorias.getConvocatoriasPublicadas();

      // Los dos maestros no dependen uno del otro: se piden a la vez.
      const [lineasConvocatorias, areas] = await Promise.all([
        maestroOpcional(() => apiMaestras.obtenerLineaConvocatoria(), "las líneas"),
        maestroOpcional(() => apiMaestras.obtenerAreas(), "las áreas"),
      ]);

      const convabiertas = (Array.isArray(convocatorias) ? convocatorias : []).filter(
        (data: any) =>
          data.estado === "Abierta" ||
          data.estado === "En espera" ||
          data.estado === "En proceso" ||
          data.estado === "Cerrada"
      );

      setPrincipalState({
        ...principalState,
        todaslasconvocatorias: convabiertas,
        convocatoriasslice: convabiertas,
        categoria_linea_convocatorias: [],
        area: areas,
        linea_estrategicas: lineasConvocatorias,
        anios: generarAnios(),
        entidad: EntidadOptions
      });
    } catch (error) {
      // Sin convocatorias no hay nada que mostrar, pero tampoco se deja la
      // pantalla girando para siempre.
      console.error("No se pudieron traer las convocatorias", error);
    } finally {
      setLoadingConvocatorias(false);
    }
  };

  const getSiguientesConvocatorias = () => {
    let convocatoriasslice = principalState.todaslasconvocatorias;
    let sumarcantidad = principalState.cantidad + principalState.incremento;
    const resultFilter = validateFilterConvocatorias(convocatoriasslice);
    convocatoriasslice = resultFilter.slice(resultFilter.length - (principalState.cantidad + principalState.incremento), resultFilter.length);
    if (sumarcantidad > resultFilter.length) {
      convocatoriasslice = resultFilter.slice(0, resultFilter.length);
      setVisibleButton(false);
    }
    setPrincipalState({ ...principalState, convocatoriasslice, cantidad: sumarcantidad });
  };

  const buscarconvocatorias = () => {
    setLoadingConvocatorias(true);
    principalState.cantidad = principalState.incremento;
    let filtro: any = principalState.todaslasconvocatorias;
    const resultFilter: any = validateFilterConvocatorias(filtro);
    let fin = resultFilter.length - 1;
    let inicio = Math.max(fin - principalState.cantidad + 1, 0);
    let convocatoriasslice = resultFilter.slice(inicio, fin + 1);
    setPrincipalState({
      ...principalState,
      convocatoriasslice
    });

    setLoadingConvocatorias(false);
  };

  const validateFilterConvocatorias = (filtro: any) => {
    if (
      principalState.buscarconvocatorias.perfil_participante &&
      principalState.buscarconvocatorias.perfil_participante !== ""
    ) {
      filtro = filtro.filter((x: any) => {
        for (const i in x.tipo_participante) {
          if (x.tipo_participante[i].value === principalState.buscarconvocatorias.perfil_participante) {
            return x;
          }
        }
      });
    }
    if (principalState.buscarconvocatorias.entidad) {
      filtro = filtro.filter((x: any) => x.entidad == principalState.buscarconvocatorias.entidad);
    }
    if (principalState.buscarconvocatorias.area && principalState.buscarconvocatorias.area !== "") {
      filtro = filtro.filter((x: any) => {
        for (const i in x.area) {
          if (x.area[i].value === principalState.buscarconvocatorias.area) {
            return x;
          }
        }
      });
    }
    if (
      principalState.buscarconvocatorias.linea_convocatoria &&
      principalState.buscarconvocatorias.linea_convocatoria !== ""
    ) {
      const lc = principalState.buscarconvocatorias.linea_convocatoria;
      const matchesLinea = (x: any) => {
        if (lc === '72') return x.nombre_convocatoria.includes('jurados');
        if (lc === '247') return x.nombre_convocatoria.includes('BEPS');
        return x.linea_convocatoria === lc;
      };
      filtro = filtro.filter(matchesLinea);
    }
    if (
      principalState.buscarconvocatorias.categoria_linea_convocatoria &&
      principalState.buscarconvocatorias.categoria_linea_convocatoria !== ""
    ) {
      filtro = filtro.filter((x: any) => {
        for (const i in x.categoria_linea_convocatoria) {
          if (
            x.categoria_linea_convocatoria[i].value === principalState.buscarconvocatorias.categoria_linea_convocatoria
          ) {
            return x;
          }
        }
      });
    }
    if (
      principalState.buscarconvocatorias.buscar_palabra_clave &&
      principalState.buscarconvocatorias.buscar_palabra_clave.trim() !== ""
    ) {
      filtro = filtro.filter((x: any) =>
        x.nombre_convocatoria
          .toUpperCase()
          .indexOf(principalState.buscarconvocatorias.buscar_palabra_clave.trim().toUpperCase()) > -1
      );
    }
    if (
      principalState.buscarconvocatorias.tipo_convocatoria &&
      principalState.buscarconvocatorias.tipo_convocatoria !== ""
    ) {
      filtro = filtro.filter((x: any) => x.tipo === principalState.buscarconvocatorias.tipo_convocatoria);
    }

    if (principalState.buscarconvocatorias.anio) {
      filtro = filtro.filter((x: any) =>
        moment(x.fecha_creacion).format('YYYY') === String(principalState.buscarconvocatorias.anio)
      );
    }
    return filtro;
  }

  /**
   * Busca con el texto que llega, sin pasar por el estado.
   *
   * El estado se actualiza de forma asincrona, asi que escribir y buscar en
   * el mismo gesto terminaba buscando lo anterior. Y el texto ya no mira
   * solo el nombre: tambien la linea, las areas y la entidad.
   */
  const buscarPorTexto = (texto: string) => {
    setLoadingConvocatorias(true);

    const encontradas = filtrarPorBusqueda(
      principalState.todaslasconvocatorias,
      texto,
      principalState.linea_estrategicas
    );

    /*
      Van todas las que responden, sin recortar, igual que la carga inicial.

      Antes se tomaban las ultimas cien, como hacia la busqueda anterior, y eso
      dejaba sin nada a las pantallas que muestran una sola entidad —Mcm,
      Mujeres, PP—: sus convocatorias son viejas y se quedaban fuera del
      recorte, asi que buscar, o incluso vaciar el campo, borraba la tabla.
    */
    setPrincipalState({
      ...principalState,
      cantidad: encontradas.length,
      convocatoriasslice: encontradas,
      buscarconvocatorias: {
        ...principalState.buscarconvocatorias,
        buscar_palabra_clave: texto,
      },
    });

    setLoadingConvocatorias(false);
  };

  const limipiarfiltros = () => {
    window.location.reload();
  };
  return {
    principalState,
    setPrincipalState,
    getConvocatorias,
    getSiguientesConvocatorias,
    limipiarfiltros,
    buscarconvocatorias,
    buscarPorTexto,
    visibleButton,
    setVisibleButton,
    setLoadingConvocatorias,
    loadingConvocatorias
  };
};
